import React from 'react';
import { GlassCard, GlassBadge } from '@/design-system';
import { TrendingUp, TrendingDown, Target, AlertTriangle, Zap } from 'lucide-react';
import { formatPercent, formatPercentTrend } from '@/utils/formatters';

interface EpicDeliveryTrend {
  period: string;
  onTrack: number;
  atRisk: number;
  offTrack: number;
  total: number;
  deliveredCount: number;
  deliveryRate: number;
}

interface EpicDeliveryTrendChartProps {
  deliveryTrend: EpicDeliveryTrend[] | null;
  isLoading: boolean;
}

const EpicDeliveryTrendChart: React.FC<EpicDeliveryTrendChartProps> = ({
  deliveryTrend,
  isLoading,
}) => {
  const getDeliveryTrend = (current: number, previous?: number) => {
    if (!previous) return { icon: Target, color: 'text-muted-foreground/60', text: 'No trend' };

    const diff = current - previous;
    if (diff > 5) return { icon: TrendingUp, color: 'text-status-success', text: formatPercentTrend(diff) };
    if (diff < -5) return { icon: TrendingDown, color: 'text-status-error', text: formatPercentTrend(diff) };
    return { icon: Target, color: 'text-muted-foreground/60', text: 'Stable' };
  };

  const getHealthStatus = (onTrack: number, total: number): { variant: 'default' | 'success' | 'warning' | 'danger'; label: string } => {
    if (total === 0) return { variant: 'default', label: 'No Data' };

    const percentage = (onTrack / total) * 100;
    if (percentage >= 80) return { variant: 'success', label: 'Healthy' };
    if (percentage >= 60) return { variant: 'warning', label: 'At Risk' };
    return { variant: 'danger', label: 'Critical' };
  };

  if (isLoading) {
    return (
      <GlassCard className="p-6 rounded-2xl">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/2 mb-4"></div>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                <div className="h-6 bg-gray-200 rounded w-full"></div>
              </div>
            ))}
          </div>
        </div>
      </GlassCard>
    );
  }

  if (!deliveryTrend || deliveryTrend.length === 0) {
    return (
      <GlassCard className="p-6 rounded-2xl">
        <h3 className="text-lg font-semibold text-foreground mb-4">EPIC Delivery Trend</h3>
        <div className="text-center text-muted-foreground/60 py-8">
          No delivery trend data available
        </div>
      </GlassCard>
    );
  }

  const latestPeriod = deliveryTrend[deliveryTrend.length - 1]!;
  const previousPeriod = deliveryTrend.length > 1 ? deliveryTrend[deliveryTrend.length - 2] : undefined;
  const trend = getDeliveryTrend(latestPeriod.deliveryRate, previousPeriod?.deliveryRate);
  const healthStatus = getHealthStatus(latestPeriod.onTrack, latestPeriod.total);

  return (
    <GlassCard className="p-6 rounded-2xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">EPIC Delivery</h3>
            <p className="text-sm text-muted-foreground/60">Performance over time</p>
          </div>
        </div>
        <GlassBadge variant={healthStatus.variant}>{healthStatus.label}</GlassBadge>
      </div>

      {/* Current Period Stats */}
      <div className="bg-[var(--surface-muted)] rounded-xl p-4 mb-5">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-muted-foreground">{latestPeriod.period}</span>
          <div className="flex items-center gap-1">
            <span className="text-2xl font-bold text-violet-600">{formatPercent(latestPeriod.deliveryRate, { decimals: 0 })}</span>
            <div className={`flex items-center ${trend.color}`}>
              <trend.icon className="w-4 h-4" />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2 text-center">
          <div className="p-2 rounded-lg bg-surface">
            <p className="text-lg font-bold text-status-success">{latestPeriod.onTrack}</p>
            <p className="text-xs text-muted-foreground/60">On Track</p>
          </div>
          <div className="p-2 rounded-lg bg-surface">
            <p className="text-lg font-bold text-status-warning">{latestPeriod.atRisk}</p>
            <p className="text-xs text-muted-foreground/60">At Risk</p>
          </div>
          <div className="p-2 rounded-lg bg-surface">
            <p className="text-lg font-bold text-status-error">{latestPeriod.offTrack}</p>
            <p className="text-xs text-muted-foreground/60">Off Track</p>
          </div>
          <div className="p-2 rounded-lg bg-surface">
            <p className="text-lg font-bold text-foreground">{latestPeriod.deliveredCount}</p>
            <p className="text-xs text-muted-foreground/60">Delivered</p>
          </div>
        </div>
      </div>

      {/* Historical Trend */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-muted-foreground">Historical Trend</h4>

        {deliveryTrend.slice(-4).map((period, index) => {
          const periodTrend = index > 0 ?
            getDeliveryTrend(period.deliveryRate, deliveryTrend[index - 1]?.deliveryRate) :
            { icon: Target, color: 'text-muted-foreground/60', text: 'Baseline' };

          return (
            <div key={period.period} className="space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{period.period}</span>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-foreground">{formatPercent(period.deliveryRate, { decimals: 0 })}</span>
                  <div className={`flex items-center gap-0.5 ${periodTrend.color}`}>
                    <periodTrend.icon className="w-3 h-3" />
                    <span className="text-xs">{periodTrend.text}</span>
                  </div>
                </div>
              </div>

              {/* Status Distribution Bar */}
              <div className="flex h-2 rounded-full overflow-hidden bg-surface-muted">
                <div
                  className="bg-status-success transition-all duration-300"
                  style={{ width: `${period.total > 0 ? (period.onTrack / period.total) * 100 : 0}%` }}
                />
                <div
                  className="bg-status-warning transition-all duration-300"
                  style={{ width: `${period.total > 0 ? (period.atRisk / period.total) * 100 : 0}%` }}
                />
                <div
                  className="bg-status-error transition-all duration-300"
                  style={{ width: `${period.total > 0 ? (period.offTrack / period.total) * 100 : 0}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* Risk Alert */}
      {latestPeriod.deliveryRate < 70 && (
        <div className="mt-4 p-3 bg-status-error-bg border border-status-error/30 rounded-xl">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-status-error" />
            <span className="text-sm font-medium text-status-error-text">Action Required</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Delivery rate is below 70%. Consider reviewing EPIC scope.
          </p>
        </div>
      )}
    </GlassCard>
  );
};

export default EpicDeliveryTrendChart;
